import { BaseSignerWalletAdapter } from '@solana/wallet-adapter-base';
import { Wallet } from '@solana/wallet-adapter-react';
import { getMint, createSetAuthorityInstruction, AuthorityType } from '@solana/spl-token';
import {
  Account,
  Connection,
  Keypair,
  PublicKey,
  SystemProgram,
  SYSVAR_RENT_PUBKEY,
  Transaction,
  TransactionInstruction,
} from '@solana/web3.js';
import { sendTransaction } from './send';
import { BN } from 'bn.js';
import { TOKEN_PROGRAM_ID } from './tokens';

export const FAUCET_PROGRAM_ID = new PublicKey(
  'HjzG5cCQnEc6vx5iRQVpJtaxd1wDwcxLtVoyMvRJD6UC',
);

const COMMITMENT = 'confirmed';

const FAUCET_SIZE = 77;

const getPDA = () =>
  PublicKey.findProgramAddress([Buffer.from('faucet')], FAUCET_PROGRAM_ID);

const getMintPubkeyFromTokenAccountPubkey = async (
  connection: Connection,
  tokenAccountPubkey: PublicKey,
) => {
  try {
    const tokenMintData = (
      await connection.getParsedAccountInfo(tokenAccountPubkey, COMMITMENT)
    ).value!.data;
    //@ts-expect-error (doing the data parsing into steps so this ignore line is not moved around by formatting)
    const tokenMintAddress = tokenMintData.parsed.info.mint;

    return new PublicKey(tokenMintAddress);
  } catch (err) {
    throw new Error(
      'Error calculating mint address from token account. Are you sure you inserted a valid token account address',
    );
  }
};

export const inspectFaucet = async (
  connection: Connection,
  faucetAddress: string,
) => {
  try {
    const faucetKey = new PublicKey(faucetAddress);
    const rawFaucetData = await connection.getParsedAccountInfo(faucetKey);
    const faucetArray = [...(rawFaucetData.value?.data as Buffer)];
    const mintPubkey = new PublicKey(faucetArray.slice(45, 77));
    const adminKey: null | PublicKey = faucetArray[1]
      ? new PublicKey(faucetArray.slice(5, 37))
      : null;
    const permittedAmount = new BN(faucetArray.slice(37, 45), undefined, 'le');
    return {
      faucetKey: faucetKey.toBase58(),
      mintPubkey: mintPubkey.toBase58(),
      adminKey: adminKey?.toBase58(),
      permittedAmount: permittedAmount.toString(10),
    };
  } catch (err) {
    throw new Error(
      `Are you sure there is a faucet at this address? If yes, just try again.`,
    );
  }
};

const buildCreateFaucetIx = (
  tokenMintPublicKey: PublicKey,
  faucetAccountPublicKey: PublicKey,
  amount: any,
  adminAddress: string | undefined,
) => {
  const keys = [
    { pubkey: tokenMintPublicKey, isSigner: false, isWritable: false },
    {
      pubkey: faucetAccountPublicKey,
      isSigner: false,
      isWritable: true,
    },
    { pubkey: SYSVAR_RENT_PUBKEY, isSigner: false, isWritable: false },
  ];

  if (adminAddress) {
    keys.push({
      pubkey: new PublicKey(adminAddress),
      isSigner: false,
      isWritable: false,
    });
  }

  return new TransactionInstruction({
    programId: FAUCET_PROGRAM_ID,
    data: Buffer.from([0, ...amount.toArray('le', 8)]),
    keys,
  });
};

export const createFaucet = async (
  connection: Connection,
  wallet: Wallet,
  tokenMintAddress: string,
  amount: any,
  adminAddress: string | undefined,
) => {
  const tokenMintPublicKey = new PublicKey(tokenMintAddress);
  const faucetAcc = new Account();

  const createFaucetIx = buildCreateFaucetIx(
    tokenMintPublicKey,
    faucetAcc.publicKey,
    amount,
    adminAddress,
  );

  const createAccIx = SystemProgram.createAccount({
    fromPubkey: wallet.adapter.publicKey!,
    newAccountPubkey: faucetAcc.publicKey,
    programId: FAUCET_PROGRAM_ID,
    space: FAUCET_SIZE,
    lamports: await connection.getMinimumBalanceForRentExemption(
      FAUCET_SIZE,
      COMMITMENT,
    ),
  });

  const transferAuthorityIx = createSetAuthorityInstruction(
    tokenMintPublicKey,
    wallet.adapter.publicKey!,
    AuthorityType.MintTokens,
    (await getPDA())[0],
  );

  const mintInfo = await getMint(connection, new PublicKey(tokenMintPublicKey));

  const transaction = new Transaction();
  transaction.add(createAccIx);
  if (!mintInfo.mintAuthority!.equals((await getPDA())[0])) {
    transaction.add(transferAuthorityIx);
  }
  transaction.add(createFaucetIx);

  await sendTransaction({
    connection,
    wallet: wallet.adapter as BaseSignerWalletAdapter,
    transaction,
    signers: [faucetAcc],
  });

  return faucetAcc.publicKey.toBase58();
};

export const buildAirdropTokensIx = async (
  amount: any,
  adminPubkey: PublicKey | null,
  tokenMintPublicKey: PublicKey,
  destinationAccountPubkey: PublicKey,
  faucetPubkey: PublicKey,
) => {
  const pubkeyNonce = await getPDA();

  const keys = [
    { pubkey: pubkeyNonce[0], isSigner: false, isWritable: false },
    {
      pubkey: tokenMintPublicKey,
      isSigner: false,
      isWritable: true,
    },
    { pubkey: destinationAccountPubkey, isSigner: false, isWritable: true },
    { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
    { pubkey: faucetPubkey, isSigner: false, isWritable: false },
  ];

  if (adminPubkey) {
    keys.push({
      pubkey: adminPubkey,
      isSigner: true,
      isWritable: false,
    });
  }

  return new TransactionInstruction({
    programId: FAUCET_PROGRAM_ID,
    data: Buffer.from([1, ...amount.toArray('le', 8)]),
    keys,
  });
};

export const airdropTokens = async (
  connection: Connection,
  wallet: Wallet,
  faucetAddress: string,
  tokenDestinationAddress: string,
  amount: any,
) => {
  const tokenDestinationPublicKey = new PublicKey(tokenDestinationAddress);
  const faucetPubkey = new PublicKey(faucetAddress);
  const tokenMintPubkey = await getMintPubkeyFromTokenAccountPubkey(
    connection,
    tokenDestinationPublicKey,
  );

  const ix = await buildAirdropTokensIx(
    amount,
    wallet.adapter.publicKey,
    tokenMintPubkey,
    tokenDestinationPublicKey,
    faucetPubkey,
  );

  const transaction = new Transaction();
  transaction.add(ix);

  await sendTransaction({
    connection,
    wallet: wallet.adapter as BaseSignerWalletAdapter,
    transaction,
  });

  return tokenDestinationPublicKey.toBase58();
};

export const airdropTokensKeypair = async (
  connection: Connection,
  wallet: Keypair,
  faucetAddress: string,
  tokenDestinationAddress: string,
  amount: any,
) => {
  const tokenDestinationPublicKey = new PublicKey(tokenDestinationAddress);
  const faucetPubkey = new PublicKey(faucetAddress);
  const tokenMintPubkey = await getMintPubkeyFromTokenAccountPubkey(
    connection,
    tokenDestinationPublicKey,
  );

  const ix = await buildAirdropTokensIx(
    amount,
    wallet.publicKey,
    tokenMintPubkey,
    tokenDestinationPublicKey,
    faucetPubkey,
  );

  const transaction = new Transaction();
  transaction.add(ix);

  const sig = await connection.sendTransaction(transaction, [wallet]);

  return tokenDestinationPublicKey.toBase58();
};

const buildCloseFaucetIx = async (
  connection: Connection,
  adminPubkey: PublicKey,
  faucetPubkey: PublicKey,
  destPubkey: PublicKey,
) => {
  const rawFaucetData = await connection.getParsedAccountInfo(faucetPubkey);
  const mintPubkey = new PublicKey(
    [...(rawFaucetData.value?.data as Buffer)].slice(45),
  );
 
  const keys = [
    { pubkey: adminPubkey, isSigner: true, isWritable: false },
    {
      pubkey: faucetPubkey,
      isSigner: false,
      isWritable: true,
    },
    { pubkey: destPubkey, isSigner: false, isWritable: true },
    { pubkey: mintPubkey, isSigner: false, isWritable: true },
    { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
    { pubkey: (await getPDA())[0], isSigner: false, isWritable: false },
  ];

  return new TransactionInstruction({
    programId: FAUCET_PROGRAM_ID,
    data: Buffer.from([2]),
    keys,
  });
};

export const closeFaucet = async (
  connection: Connection,
  wallet: Wallet,
  faucetAddress: string,
  destAddress: string,
) => {
  const destPubkey = new PublicKey(destAddress);
  const faucetPubkey = new PublicKey(faucetAddress);

  const closeFaucetIx = await buildCloseFaucetIx(
    connection,
    wallet.adapter.publicKey!,
    faucetPubkey,
    destPubkey,
  );
  const transaction = new Transaction();
  transaction.add(closeFaucetIx);

  await sendTransaction({
    connection,
    wallet: wallet.adapter as BaseSignerWalletAdapter,
    transaction,
  });

  return faucetPubkey.toBase58();
};
